import { useParams } from 'react-router-dom'

/**
 * The URL params which are used to navigate app management routes
 */
export type AppURLParams = {
  /**
   * The slug of the app which is being managed. This is always present.
   */
  appSlug: string
  /**
   * The slug of the organization which the app belongs to. This will not be present
   * for personal organizations.
   */
  organizationSlug?: string
  /**
   * The user ID of the user who owns the app, if the app is a personal app being viewed
   * by an admin.
   */
  userId?: string
}

/**
 * A helper type to resolve the URL params with the provided type, only if the provided type is
 * not the default void type.
 */
export type ResolvedAppURLParams<T> = T extends void
  ? AppURLParams
  : AppURLParams & T

/**
 * Hook to extract app management URL params from the current location.
 *
 * @template T If provided, the hook will return the URL params intersected with this type. It
 * is important to note the intersection is not checked, so the caller should handle type-safety.
 */
export function useAppURLParams<T = void>(): ResolvedAppURLParams<T> {
  const params = useParams<AppURLParams>()

  if (!params.appSlug) {
    throw new Error('Invalid app management URL: app slug is required')
  }

  return params as ResolvedAppURLParams<T>
}
